import React, { useMemo, useState, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { Skeleton } from "@mantine/core";
import { useReducedMotion } from "@mantine/hooks";
import useInsightsDrilldown from "hooks/useInsightsDrilldown";
import {
  getPatternFindings,
  getPatternRows,
  patternDimensions,
} from "./meetingPatternsData";
import css from "./MeetingPatterns.module.scss";

export default function MeetingPatterns({
  data,
  period,
  self,
  uid,
  collection = "orgCalculations",
  isLoading,
  isError,
}) {
  const [dimension, setDimension] = useState("byDuration");
  const reducedMotion = useReducedMotion();
  const { openDrilldown } = useInsightsDrilldown();
  const rows = useMemo(
    () => getPatternRows(data?.[dimension], dimension),
    [data, dimension],
  );
  const findings = getPatternFindings(rows);
  const total = rows.reduce((sum, row) => sum + row.total, 0);
  const openBucket = (row, meetingType = "All") => {
    const ratedOnly = meetingType === "Rated";
    if (!uid || !row?.total) return;
    const types =
      meetingType === "All" || ratedOnly
        ? ["internal", "external"]
        : [meetingType.toLowerCase()];
    openDrilldown({
      name: row.label,
      ...(ratedOnly ? { ratedOnly: true } : {}),
      type: "Meetings",
      meetingType,
      // `self` is an array on the combined My time page, which reads both roles.
      redisKeys: [self].flat().flatMap((perspective) =>
        row.keys.flatMap((key) =>
          types.map(
            (type) =>
              `users:${uid}:${collection}:meetingsDistribution:${period}:${perspective}:${dimension}:${key}:${type}`,
          ),
        ),
      ),
    });
  };
  // This wrapper version recreates the chart when onEvents changes identity.
  const clickHandler = useRef();
  clickHandler.current = (params) =>
    openBucket(
      rows[params.dataIndex],
      params.seriesName === "Rating" ? "Rated" : params.seriesName,
    );
  const chartEvents = useMemo(
    () => ({ click: (params) => clickHandler.current(params) }),
    [],
  );
  const option = useMemo(
    () => ({
      animation: !reducedMotion,
      animationDuration: 400,
      animationDurationUpdate: 550,
      animationEasingUpdate: "cubicInOut",
      grid: { top: 24, left: 34, right: 30, bottom: 62 },
      tooltip: {
        trigger: "axis",
        confine: true,
        renderMode: "richText",
        formatter: (params) => {
          const row = rows[params[0]?.dataIndex];
          return row
            ? `${row.label}\n${row.total} meetings total\nInternal: ${row.internal}\nExternal: ${row.external}\nAverage rating: ${row.rating === null ? "No ratings" : `${row.rating.toFixed(1)} / 5`}\n${row.ratedMeetings} of ${row.total} meetings rated\nClick a dot for rated meetings or a bar for its meetings.`
            : "";
        },
      },
      xAxis: {
        type: "category",
        data: rows.map((row) => row.label),
        axisTick: { show: false },
        axisLine: { lineStyle: { color: "#e1e3e8" } },
        axisLabel: {
          interval: 0,
          color: "#5d6070",
          fontSize: 12,
          margin: 14,
          formatter: (label, index) =>
            `{count|${rows[index].total} ${rows[index].total === 1 ? "meeting" : "meetings"}}\n${label}`,
          rich: {
            count: { color: "#1c1e2b", fontWeight: 600, lineHeight: 22 },
          },
        },
      },
      yAxis: [
        {
          type: "value",
          min: 0,
          max: Math.max(
            10,
            Math.ceil((Math.max(...rows.map((row) => row.total)) * 1.15) / 10) *
              10,
          ),
          splitNumber: 2,
          minInterval: 1,
          axisLabel: { color: "#8b8e9e", fontSize: 11 },
          splitLine: { lineStyle: { type: "dashed", color: "#eceef2" } },
        },
        {
          type: "value",
          min: 0,
          max: 5,
          interval: 1,
          name: "Rating",
          nameTextStyle: { color: "#8b8e9e", fontSize: 11 },
          axisLabel: { color: "#8b8e9e", fontSize: 11 },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          id: "internal",
          name: "Internal",
          type: "bar",
          stack: "meetings",
          barMaxWidth: 64,
          data: rows.map((row) => row.internal),
          itemStyle: { color: "#cfdbe7" },
          emphasis: {
            itemStyle: { shadowBlur: 8, shadowColor: "rgba(28,30,43,.18)" },
          },
        },
        {
          id: "external",
          name: "External",
          type: "bar",
          stack: "meetings",
          barMaxWidth: 64,
          data: rows.map((row) => row.external),
          itemStyle: { color: "#39a8f0", borderRadius: [5, 5, 0, 0] },
          emphasis: {
            itemStyle: { shadowBlur: 8, shadowColor: "rgba(28,30,43,.18)" },
          },
        },
        {
          id: "rating",
          name: "Rating",
          type: "line",
          yAxisIndex: 1,
          data: rows.map((row) => row.rating),
          lineStyle: { opacity: 0 },
          symbol: "circle",
          symbolSize: 11,
          showSymbol: true,
          connectNulls: false,
          itemStyle: {
            color: "#fff",
            borderColor: "#e8590c",
            borderWidth: 2.5,
          },
          label: {
            show: true,
            position: "right",
            color: "#b4540a",
            fontSize: 11,
            backgroundColor: "#fff",
            padding: [1, 3],
            formatter: (params) =>
              params.value == null
                ? ""
                : `${Number(params.value).toFixed(1)}\n{sample|${rows[params.dataIndex].ratedMeetings} rated}`,
            rich: {
              sample: { color: "#737789", fontSize: 10, lineHeight: 16 },
            },
          },
          z: 5,
        },
      ],
    }),
    [rows, reducedMotion],
  );
  return (
    <section
      className={css.card}
      aria-label="Meeting patterns"
      aria-busy={isLoading}
    >
      <header className={css.header}>
        <div>
          <h3>Meeting patterns</h3>
          <p>
            Bars show all meetings. Dots show the average of rated meetings.
          </p>
        </div>
        <div className={css.controls}>
          <div className={css.legend}>
            <span>
              <i className={css.internal} />
              Internal
            </span>
            <span>
              <i className={css.external} />
              External
            </span>
            <span>
              <i className={css.rating} />
              Rating
            </span>
          </div>
          <div className={css.tabs} role="group" aria-label="Group meetings">
            {Object.entries(patternDimensions).map(([key, value]) => (
              <button
                type="button"
                key={key}
                aria-pressed={dimension === key}
                onClick={() => setDimension(key)}
              >
                {value.label}
              </button>
            ))}
          </div>
        </div>
      </header>
      {isLoading ? (
        <Skeleton height={300} mt={20} />
      ) : isError ? (
        <p role="alert">Unable to load meeting patterns.</p>
      ) : !data?.[dimension] ? (
        <p className={css.empty}>
          Meeting patterns are not available for this period.
        </p>
      ) : (
        <div className={css.body}>
          <div className={css.chartArea}>
            <div className={css.chartScroll}>
              <ReactECharts
                option={option}
                notMerge={false}
                style={{ height: 310, minWidth: 440 }}
                opts={{ renderer: "svg" }}
                onEvents={chartEvents}
              />
            </div>
            <div
              className={css.bucketLinks}
              aria-label="View meetings by group"
            >
              {rows.map((row) => (
                <button
                  type="button"
                  key={row.label}
                  disabled={!uid || !row.total}
                  onClick={() => openBucket(row)}
                >
                  View all {row.total} · {row.label}
                </button>
              ))}
            </div>
          </div>
          <aside className={css.findings}>
            <h4>What stands out</h4>
            {findings.length ? (
              findings.map((text) => <p key={text}>{text}</p>)
            ) : (
              <p>
                {total
                  ? "No ratings available."
                  : "No meetings in this period."}
              </p>
            )}
          </aside>
        </div>
      )}
    </section>
  );
}
